import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { SectionsEnum } from '../../enums/sections.enum';

@Injectable({
  providedIn: 'root',
})
export class SharedStateService {

  private categorySectionSource = new BehaviorSubject<SectionsEnum>(SectionsEnum.DETAILS);
  public sectionCompletionSource = new BehaviorSubject<{ [key in SectionsEnum]?: boolean }>(
    this.initializeSectionCompletion(SectionsEnum.CHAPTERS)
  );

  public categoryOrCourseData = new BehaviorSubject<{ name: string, id: string }>(null);

  public contentEditView = new BehaviorSubject<boolean>(false);
  iscontentEditView = this.contentEditView.asObservable();

  currentCategorySection = this.categorySectionSource.asObservable();
  sectionCompletionStatus = this.sectionCompletionSource.asObservable();

  private courseSectionSource = new BehaviorSubject<SectionsEnum>(SectionsEnum.DETAILS);
  public courseSectionCompletionSource = new BehaviorSubject<{ [key in SectionsEnum]?: boolean }>(
    this.initializeSectionCompletion(SectionsEnum.CHAPTERS)
  );

  currentCourseSection = this.courseSectionSource.asObservable();
  courseSectionCompletionStatus = this.courseSectionCompletionSource.asObservable();

  hideSteps = new BehaviorSubject<boolean>(false);
  hideSteps$ = this.hideSteps.asObservable();

  public totalStepsCount = new BehaviorSubject<number>(0);
  public currentStepIndex = new BehaviorSubject<number>(0);

  setTotalSteps(count: number) {
    this.totalStepsCount.next(count);
  }

  setCurrentStepIndex(index: number) {
    this.currentStepIndex.next(index);
  }

  isOnLastStep(): boolean {
    const total = this.totalStepsCount.value;
    const current = this.currentStepIndex.value;
    return current === total - 1;
  }

  private saveEditedContent = new Subject<void>();
  saveEditedContent$ = this.saveEditedContent.asObservable();

  private saveAsDraftSubject = new Subject<boolean>();
  saveAsDraft$ = this.saveAsDraftSubject.asObservable();

  public canSaveAsDraftSubject = new Subject<boolean>();
  canSaveAsDraft$ = this.canSaveAsDraftSubject.asObservable();

  public hasShownLeavePopup = false;

  discardedChangesCb: () => Promise<void> | void = null;

  resetShownLeavePopup() {
    this.hasShownLeavePopup = false;
  }

  triggerSaveAsDraft(bool: boolean) {
    this.saveAsDraftSubject.next(bool);
  }

  triggerSaveEditedContent() {
    this.saveEditedContent.next();
  }

  changeCategorySection(section: SectionsEnum) {
    this.categorySectionSource.next(section);
  }

  changeCourseSection(section: SectionsEnum) {
    this.courseSectionSource.next(section);
  }

  resetCourseSectionState() {
    this.courseSectionSource.next(SectionsEnum.DETAILS);
    this.courseSectionCompletionSource.next(
      this.initializeSectionCompletion(SectionsEnum.CHAPTERS)
    );
  }

  changeSection(section: SectionsEnum) {
    this.categorySectionSource.next(section);
    this.courseSectionSource.next(section);
  }

  updateCourseSectionCompletion(section: SectionsEnum, completed: boolean) {
    const currentCourseStatus = this.courseSectionCompletionSource.value;
    currentCourseStatus[section] = completed;
    this.courseSectionCompletionSource.next(currentCourseStatus);
  }

  updateCategorySectionCompletion(section: SectionsEnum, completed: boolean) {
    const currentCategoryStatus = this.sectionCompletionSource.value;
    currentCategoryStatus[section] = completed;
    this.sectionCompletionSource.next(currentCategoryStatus);
  }

  initializeSectionCompletion(excludeKey: SectionsEnum) {
    const { [excludeKey]: _, ...rest } = {
      [SectionsEnum.DETAILS]: false,
      [SectionsEnum.THUMBNAIL]: false,
      [SectionsEnum.CHAPTERS]: false,
      [SectionsEnum.COURSES]: false,
      [SectionsEnum.TRANSLATIONS]: false,
      [SectionsEnum.CLIENTS]: false,
    };
    return rest;
  }
}
